<template>
    <div id="contact">
        <div class="modal">
            <div class="success-banner" v-if="submitted">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="12" fill="#2A3854"/><path d="M7 12.5l3.5 3.5 6.5-7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <div>
                    <strong>Message sent!</strong>
                    <p>Thank you for getting in touch. We'll get back to you soon.</p>
                </div>
            </div>

            <form class="contact-form" v-else @submit.prevent="submit">
                <fieldset>
                    <div>
                        <label class="label required" for="name">Name</label>
                        <p class="error-message" v-if="!validName && name.allowValidation">A name is required</p>
                        <input type="text" name="name" id="name" required v-model="name.value" v-on:blur="name.allowValidation = true" v-on:focus="name.allowValidation = false">
                    </div>
                    <div>
                        <label class="label required" for="email">Email</label>
                        <p class="error-message" v-if="!validEmail && email.allowValidation">Please enter a valid email address.</p>
                        <input type="email" name="email" id="email" required :class="{ email , error: !email.valid }" v-model="email.value" v-on:blur="email.allowValidation = true" v-on:focus="email.allowValidation = false">
                    </div>
                    <div>
                        <label class="label" for="subject">Subject</label>
                        <input type="text" name="subject" id="subject" v-model="subject.value">
                    </div>
                    <div>
                        <label class="label required" for="textarea">Message</label>
                        <textarea class="message" name="message" id="textarea" required v-model="message.value" v-on:blur="message.allowValidation = true" v-on:focus="message.allowValidation = false"></textarea>
                    </div>
                    <div>
                        <div class="cf-turnstile" :data-sitekey="turnstileSiteKey" data-theme="light"></div>
                    </div>
                    <div>
                        <button type="submit" :disabled="submitting">
                            {{ submitting ? 'Sending...' : 'Send ➔' }}
                        </button>
                        <p class="error-message" v-if="error">Something went wrong, please try again.</p>
                    </div>
                </fieldset>
            </form>
        </div>
    </div>
</template>

<script>
import { ScrollTrigger } from "gsap/ScrollTrigger";

const FUNCTION_URL = "https://us-central1-reset-staging-2022.cloudfunctions.net/sendContactEmail";

export default {
    data(){
        return{
            name: { value: "", allowValidation: false },
            email: { value: "", allowValidation: false },
            subject: { value: "" },
            message: { value: "", allowValidation: false },
            submitted: false,
            submitting: false,
            error: false,
            turnstileSiteKey: process.env.VUE_APP_TURNSTILE_SITE_KEY,
            turnstileToken: null
        }
    },
    props: ['fadeIn'],
    computed: {
        validName(){ return this.name.value ? true : false },
        validEmail(){ return this.isEmail(this.email.value) }
    },
    mounted() {
        // Load Turnstile script
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);

        // Turnstile callback
        window.onTurnstileSuccess = (token) => {
            this.turnstileToken = token;
        };

        // Set callback on widget after script loads
        script.onload = () => {
            const widget = document.querySelector('.cf-turnstile');
            if (widget) widget.setAttribute('data-callback', 'onTurnstileSuccess');
        };

        this.fadeIn();
        setTimeout(() => { ScrollTrigger.refresh() }, 1000);
    },
    methods: {
        async submit() {
            if (!this.validName || !this.validEmail) return;

            if (!this.turnstileToken) {
                alert('Please complete the CAPTCHA');
                return;
            }

            this.submitting = true;
            this.error = false;

            try {
                const response = await fetch(FUNCTION_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: this.name.value,
                        email: this.email.value,
                        subject: this.subject.value,
                        message: this.message.value,
                        turnstileToken: this.turnstileToken
                    })
                });

                if (!response.ok) throw new Error('Failed to send');

                this.submitted = true;
                this.name = { value: "", allowValidation: false };
                this.email = { value: "", allowValidation: false };
                this.subject = { value: "" };
                this.message = { value: "", allowValidation: false };
                this.turnstileToken = null;

            } catch(err) {
                console.error(err);
                this.error = true;
            } finally {
                this.submitting = false;
            }
        },
        isEmail(value) {
            var emailRegExp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return emailRegExp.test(value);
        }
    }
}
</script>

<style lang="scss">

#contact {
    @include for-phone-only(){
        padding: 1em 0;
    }
    padding: 3em 0;
    align-items: center;
    justify-content: center;
    height: 100%;
}

.success-banner {
    display: flex;
    align-items: flex-start;
    gap: 1em;
    background: #f0f4ff;
    border: 1px solid #2A3854;
    border-radius: 8px;
    padding: 1.5em;
    margin: 2em auto;
    width: 40%;
    @include for-phone-only() {
        width: 80%;
    }

    svg {
        flex-shrink: 0;
        margin-top: 2px;
    }

    strong {
        display: block;
        color: #2A3854;
        font-size: 1.1em;
        margin-bottom: 0.25em;
    }

    p {
        margin: 0;
        color: #555;
        font-size: 0.9em;
    }
}

.contact-form {
    @include for-phone-only(){
        margin: 20px auto;
    }
    margin: 50px auto;

    fieldset {
        border: none;
    }
    div {
        position: relative;
        margin: 20px 0;
    }
    .label {
        color: #2c3e50;
        margin-bottom: 10px;
        display: inline-block;
        line-height: 2rem;
        width: 50%;
    }
    input{
        color: #2c3e50;
        display: block;
        width: 94%;
        appearance: none;
        padding: 10px 3%;
        border-top: none;
        border-right: none;
        border-left: none;
        border-bottom: 1px solid #cfd9db;
        background-color: #ffffff;
    }
    .required::after{
        margin: 0 0 0 0.3em;
        font-size: 0.8em;
        width: 4px;
        height: 4px;
        color: #b71c1c;
        content: '*';
    }
    input[type="text"]:focus,
    input[type="email"]:focus,
    textarea:focus{
        outline: none;
        border-color: $red;
        box-shadow: 0px 3px 10px 1px rgba(0,0,0,0.1);
    }
    textarea{
        color: #2b3e51;
        display: block;
        width: 94%;
        appearance: none;
        padding: 10px 3%;
        border: 1px solid #cfd9db;
        background-color: #ffffff;
        border-radius: 0.3rem;
        min-height: 120px;
        resize: vertical;
        overflow: auto;
        font-family: "Gantari", helvetica, sans-serif;
    }
    .error-message {
        display: inline-block;
        margin: 0px;
        padding: 0;
        color: #ff0000;
        font-size: .7rem;
        text-align: center;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }
    .confirmationMsg{
        display: inline-block;
        margin: 0 0 0 1rem;
        padding: 0;
        font-size: .7rem;
        text-align: center;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        color: #2b3e51;
    }
    button[type="submit"] {
        font-family: inherit;
        padding: 1em 2.5em;
        border-radius: 25px;
        border: none;
        cursor: pointer;

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }
}
</style>
